import React, { useEffect, useState } from 'react';
import { Button, Input, Space, Upload } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import style from "../styles/chat.module.sass"
import { UploadtoAWS, getKey } from '../functions';
import Typing from './Typing';
import { Message, MessageBoxProps, FileType, MessageToSave, MessageFileUploaded, UploadChatFileProps, User } from '../interfaces/interfaces';
import { UploadChangeParam } from 'antd/lib/upload/interface';
import { MessageAttachment } from './MessageItem';
import { useChat } from '../contexts/ChatContext';

interface MessageFormProps extends MessageBoxProps {
    user: User
}

export const MessageForm = (props: MessageFormProps) => {

    const { userTyping, activeRoom, user, setUserTyping, setMessages, socket, channelName } = props;
    const { replyMessage, setReplyMessage } = useChat()

    const initialMessage: Message = {
        id: getKey(),
        room: activeRoom,
        user: user!,
        message: '',
        files: [],
        read: false,
        sender: user!.id,
        event_type: "new_message",
        reply_message: replyMessage
    };

    const [message, setMessage] = useState<Message>(initialMessage);
    const [fileList, setFileList] = useState<FileType[]>([]);


    useEffect(() => {
        setMessage((prev) => ({
            ...prev,
            channel_name: channelName,
            room: activeRoom,
        }));
    }, [channelName, activeRoom]);


    const handleDeleteAttachment = (uid: string | undefined) => {
        const updatedFileList = fileList.filter(item => item.uid !== uid)
        const fileUrls = updatedFileList
            .filter(item => item.uid !== uid)
            .map(item => {
                return {
                    url: URL.createObjectURL(item.originFileObj!),
                    name: item.name,
                    uid: item.uid
                }
            });

        handleUpdate({
            name: "files", value: fileUrls
        });
        setFileList(updatedFileList)

    }

    const [messageToSent, setMessageToSent] = useState<MessageToSave | null>(null);

    useEffect(() => {
        if (message.message.trim() === "") {
            setUserTyping(null)
        }
    }, [message.message]);

    const ResetChatForm = () => {
        setMessage(initialMessage)
        setFileList([])
        setLoading(false)
        setMessageToSent(null)
        setReplyMessage(null)
    }


    const [loading, setLoading] = useState(false);
    const handleSendMessage = async () => {
        setLoading(true);

        if (message.files.length > 0) {
            const fileListPrepare = fileList
                .filter(item => item.originFileObj !== undefined)
                .map(item => ({
                    file: item.originFileObj as File,
                    url: URL.createObjectURL(item.originFileObj!),
                    percent: 0,
                    uid: item.uid,
                    name: item.name,
                    uploadedUrl: null
                }));

            const now = new Date
            const messageWithDate = { ...message, date_create: formatDate(now) }
            const tempMessage = { ...messageWithDate, files: fileListPrepare };

            setMessages(prev => [...prev, tempMessage])


            try {
                const uploadPromises = tempMessage.files.map(async (fileToUpload) => {
                    try {
                        if (!fileToUpload.file) {
                            return;
                        }
                        const uploadResult = await UploadtoAWS(fileToUpload.file, percent => {
                            setMessages(prevMessages => {
                                return prevMessages.map(item => {
                                    if (item.id === message.id) {

                                        const updatedFiles = item.files.map(file =>
                                            file.uid === fileToUpload.uid ? { ...file, percent } : file
                                        );
                                        return { ...item, files: updatedFiles };
                                    }
                                    return item;
                                });
                            });
                        }, true);

                        // console.log("Upload Result:", uploadResult);
                        if (!uploadResult) return
                        return { name: fileToUpload.name, url: uploadResult.Location };

                    } catch (error) {
                        console.error("Error uploading file:", error);
                        return null
                    }
                });

                const uploadedFiles = await Promise.all(uploadPromises);
                // console.log("Uploaded Files:", uploadedFiles);
                const validUploadedFiles: MessageFileUploaded[] = uploadedFiles
                    .filter(file => file && typeof file.name === 'string' && typeof file.url === 'string') as MessageFileUploaded[];

                setMessageToSent({ ...message, files: validUploadedFiles, reply_message: replyMessage });


            } catch (error) {
                console.error("Error in uploading files:", error);
            } finally {
                setLoading(false);
            }
        } else {
            const finalMessage = { ...message, reply_message: replyMessage }
            const now = new Date
            setMessageToSent(finalMessage)
            const messageWithDate = { ...finalMessage, date_create: formatDate(now) }
            setMessages(prev => [...prev, messageWithDate])

        }



    };



    useEffect(() => {
        if (socket) {

            const sendMessage = async () => {
                console.log('messageToSent', messageToSent)
                socket.send(JSON.stringify(messageToSent));
                ResetChatForm()
            };

            if (messageToSent && (messageToSent.message.trim() !== '' || message.files.length > 0)) {
                sendMessage();
            }
        }

    }, [messageToSent, socket]);


    const handleUpdate = (update: { name: string; value: any }) => {
        setMessage((prev) => ({
            ...prev,
            [update.name]: update.value,
        }));
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (message.message.trim() !== '' || message.files.length > 0)) {
            // Send the message and clear the input
            handleSendMessage();
            return;
        }


        const data = {
            channel_name: channelName,
            event_type: 'typing',
            room: activeRoom,
            sender: user && user.id,
        };

        // console.log('message.message', message.message)

        if (message.message.trim() !== "") {
            socket && socket.send(JSON.stringify(data));
        }
    };

    // console.log('replyMessage', replyMessage)
    return (
        <div>
            <div className={style.text_field}>
                {replyMessage &&
                    <div className={style.reply_message_box}>
                        <i className="las la-reply"
                            style={{ fontSize: "1.2rem" }}
                        ></i>
                        <div className={style.reply_message}>
                            <div className={style.reply_message_text}>
                                {replyMessage.message}

                            </div>

                            <div className={style.reply_to}>
                                {replyMessage.user.name}
                            </div>
                        </div>
                        <div className={style.cancel_reply} onClick={() => setReplyMessage(null)}>
                            <i className="las la-times"></i>
                        </div>
                    </div>
                }

                {fileList.length > 0 &&

                    <div className={`${style.pending_file_list} mb-5`}>
                        {!loading && message.files.map((item, index) => (
                            <div className={style.message_attachment_temp} key={"file-" + index}>
                                <div className={`${style.message_attachment_temp_delete} delete`}
                                    onClick={() => handleDeleteAttachment(item.uid)}
                                ></div>
                                <MessageAttachment
                                    url={item.url}
                                    name={item.name}
                                    percent={item.percent}
                                    className={style.file_preview} />
                                {/* <div className={style.file_name}>{item.name}</div> */}
                            </div>
                        ))}
                    </div>
                }

                <div className={style.message_input_box}>
                    {userTyping && (userTyping !== user.id) &&
                        <Typing />
                    }
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            placeholder='Type something...'
                            value={message.message}
                            onKeyDown={handleKeyPress}
                            onChange={e => handleUpdate({ name: "message", value: e.target.value })}

                        />
                        {(message.message.trim() !== "" || message.files.length > 0) &&

                            <Button type="primary" className='px-2'
                                onClick={handleSendMessage}
                                disabled={loading}
                            >
                                <i className="las la-paper-plane" style={{ fontSize: ".8em" }}></i>
                            </Button>
                        }
                    </Space.Compact>
                    <UploadChatFile
                        handleUpdate={handleUpdate}
                        loading={loading}
                        fileList={fileList}
                        setFileList={setFileList} />
                </div>

            </div>
        </div>
    )
}




const UploadChatFile: React.FC<UploadChatFileProps> = (props) => {
    const { fileList, setFileList, handleUpdate } = props;

    const handleChange = (info: UploadChangeParam<FileType>) => {
        const { file, fileList } = info;

        const fileUrls = fileList
            .filter(item => item.originFileObj !== undefined)
            .map(item => {
                return {
                    url: URL.createObjectURL(item.originFileObj!),
                    name: file.name,
                    uid: file.uid
                }
            });

        handleUpdate({
            name: "files", value: fileUrls
        });

        // Update the component's fileList state
        setFileList(fileList);
    };


    return (
        <div className="upload-file-box">
            <Upload
                fileList={fileList}
                onChange={handleChange}
                showUploadList={false}
                beforeUpload={() => false}
                multiple={true}
            >
                <Button icon={<PaperClipOutlined />} />
            </Upload>
        </div>
    );
};


const formatDate = (date: Date): string => {
    const tzo = -date.getTimezoneOffset(),
        dif = tzo >= 0 ? '+' : '-';

    const pad = (num: number): string => {
        const norm = Math.floor(Math.abs(num));
        return (norm < 10 ? '0' : '') + norm;
    };

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${String(date.getMilliseconds()).padStart(6, '0')}${dif}${pad(tzo / 60)}:${pad(tzo % 60)}`;
};